import { ArrowLeft, ChevronDown, FolderKey, Plus, Trash2, User } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import {
  AdminBulkActionRow,
  AdminSectionCard,
  ConfirmDialog,
  EmptyState,
  IconButton,
  Modal,
  Pill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import {
  BUTTON_GHOST_ADMIN,
  BUTTON_PRIMARY,
  FIELD_INPUT,
  MENU_CONTENT,
  MENU_ITEM_BASE,
} from '@/shared/ui-class';
import {
  SEED_ADMIN_PROJECTS,
  SEED_ADMIN_USERS,
  type AdminProjectMember,
} from '@/versions/v1/shared/admin-seed';
import { useClickOutside } from '@/versions/v1/chrome/sidebar/AccountMenu';
import {
  AdminSceneShell,
  formatAdminDate,
} from './admin-shared';
import { filterUsersForAddMember } from './SignupsScene.transform';
import {
  filterProjects,
  memberAssignedAt,
  projectThreadCount,
  roleLabel,
  rolePillTone,
} from './ProjectsScene.transform';

const ROLE_OPTIONS: {
  value: AdminProjectMember['role'];
  label: string;
  description: string;
}[] = [
  {
    value: 'project_viewer',
    label: 'Viewer',
    description: 'view + ask AI',
  },
  {
    value: 'project_member',
    label: 'Member',
    description: 'create + edit inside the project',
  },
  {
    value: 'project_admin',
    label: 'Admin',
    description: 'members, settings, delete',
  },
];

function RoleMenu({
  role,
  onChange,
}: {
  role: AdminProjectMember['role'];
  onChange: (role: AdminProjectMember['role']) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-1"
      >
        <Pill tone={rolePillTone(role)}>{roleLabel(role)}</Pill>
        <ChevronDown className="h-3.5 w-3.5 text-muted" />
      </button>
      {open ? (
        <div className={cn(MENU_CONTENT, 'absolute left-0 top-full z-50 mt-1 min-w-[220px]')}>
          {ROLE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={cn(MENU_ITEM_BASE, 'w-full flex-col items-start gap-0.5 py-2')}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              <span className="font-medium">
                {option.label} — {option.description}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ProjectsScene() {
  const [projects, setProjects] = useState(SEED_ADMIN_PROJECTS);
  const [query, setQuery] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addQuery, setAddQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [addRole, setAddRole] = useState<AdminProjectMember['role']>('project_viewer');
  const [removeTarget, setRemoveTarget] = useState<AdminProjectMember | null>(null);

  const filteredProjects = useMemo(
    () => filterProjects(projects, query),
    [projects, query],
  );

  const selectedProject = projects.find(
    (project) => project.id === selectedProjectId,
  );

  const updateProjectMembers = (
    projectId: string,
    updater: (members: AdminProjectMember[]) => AdminProjectMember[],
  ) => {
    setProjects((prev) =>
      prev.map((project) => {
        if (project.id !== projectId) return project;
        const members = updater(project.members);
        return { ...project, members, memberCount: members.length };
      }),
    );
  };

  const memberEmails = useMemo(
    () =>
      new Set(
        (selectedProject?.members ?? []).map((member) => member.email.toLowerCase()),
      ),
    [selectedProject],
  );

  const searchResults = useMemo(
    () => filterUsersForAddMember(SEED_ADMIN_USERS, addQuery, memberEmails),
    [addQuery, memberEmails],
  );

  const addMember = () => {
    if (!selectedProject || !selectedUserId) return;
    const user = SEED_ADMIN_USERS.find((entry) => entry.id === selectedUserId);
    if (!user) return;

    const newMember: AdminProjectMember = {
      userId: user.id,
      email: user.email,
      name: user.name,
      role: addRole,
    };

    updateProjectMembers(selectedProject.id, (members) => [...members, newMember]);
    setAddQuery('');
    setSelectedUserId(null);
    setAddRole('project_viewer');
    setAddModalOpen(false);
  };

  const removeMember = (member: AdminProjectMember) => {
    if (!selectedProject) return;
    updateProjectMembers(selectedProject.id, (members) =>
      members.filter((entry) => entry.userId !== member.userId),
    );
  };

  if (selectedProject) {
    return (
      <AdminSceneShell
        sceneName="ProjectsScene"
        title="Project access"
        description="Assign cross-tenant project membership — viewer, member, or admin."
        stackClassName="space-y-4"
      >
        <Commentable name="MembersView">
          <div className="space-y-6">
            <button
              type="button"
              onClick={() => setSelectedProjectId(null)}
              className={cn(BUTTON_GHOST_ADMIN, 'inline-flex items-center gap-2')}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              All projects
            </button>

            <AdminSectionCard>
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                {selectedProject.name}
              </h2>
              <p className="mt-1 text-sm text-muted">
                Owner: {selectedProject.ownerEmail}
                {selectedProject.ownerName ? ` (${selectedProject.ownerName})` : ''}
              </p>
            </AdminSectionCard>

            <AdminSectionCard
              title="Members"
              actions={
                <button
                  type="button"
                  onClick={() => setAddModalOpen(true)}
                  className={cn(BUTTON_PRIMARY, 'inline-flex items-center gap-1.5')}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  Add member
                </button>
              }
            >
              {selectedProject.members.length === 0 ? (
                <EmptyState
                  embedded
                  icon={<User className="h-8 w-8 text-zinc-400" />}
                  title="No members yet"
                />
              ) : (
                <Table wrap={false}>
                  <THead>
                    <TR>
                      <TH className="w-8" />
                      <TH>Email</TH>
                      <TH>Name</TH>
                      <TH>Role</TH>
                      <TH>Assigned</TH>
                      <TH className="w-12" aria-label="Actions" />
                    </TR>
                  </THead>
                  <TBody>
                    {selectedProject.members.map((member) => (
                      <TR key={member.userId}>
                        <TD className="w-8 text-zinc-400">
                          <User className="h-3.5 w-3.5" />
                        </TD>
                        <TD>{member.email}</TD>
                        <TD>{member.name ?? '—'}</TD>
                        <TD>
                          <RoleMenu
                            role={member.role}
                            onChange={(role) =>
                              updateProjectMembers(selectedProject.id, (members) =>
                                members.map((entry) =>
                                  entry.userId === member.userId
                                    ? { ...entry, role }
                                    : entry,
                                ),
                              )
                            }
                          />
                        </TD>
                        <TD>{formatAdminDate(memberAssignedAt(member))}</TD>
                        <TD className="text-right">
                          <IconButton
                            label={`Remove ${member.email}`}
                            onClick={() => setRemoveTarget(member)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </IconButton>
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              )}
            </AdminSectionCard>
          </div>
        </Commentable>

        <Modal open={addModalOpen} onOpenChange={setAddModalOpen} title="Add member">
          <div className="space-y-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                Search users
              </span>
              <input
                type="search"
                value={addQuery}
                onChange={(event) => {
                  setAddQuery(event.target.value);
                  setSelectedUserId(null);
                }}
                className={FIELD_INPUT}
                placeholder="email@example.com"
              />
            </label>
            {searchResults.length > 0 ? (
              <ul className="max-h-40 overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
                {searchResults.map((user) => (
                  <li key={user.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedUserId(user.id)}
                      className={cn(
                        'w-full px-3 py-2 text-left text-sm hover:bg-zinc-50 dark:hover:bg-zinc-900',
                        selectedUserId === user.id && 'bg-zinc-100 dark:bg-zinc-800',
                      )}
                    >
                      {user.email}
                      {user.name ? ` — ${user.name}` : ''}
                    </button>
                  </li>
                ))}
              </ul>
            ) : addQuery.trim() ? (
              <p className="text-sm text-muted">No users match.</p>
            ) : null}
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                Role
              </span>
              <select
                value={addRole}
                onChange={(event) =>
                  setAddRole(event.target.value as AdminProjectMember['role'])
                }
                className={FIELD_INPUT}
              >
                {ROLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} — {option.description}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={!selectedUserId}
              onClick={addMember}
              className={BUTTON_PRIMARY}
            >
              Add
            </button>
          </div>
        </Modal>

        <ConfirmDialog
          open={removeTarget !== null}
          onOpenChange={(open) => {
            if (!open) setRemoveTarget(null);
          }}
          title="Remove member?"
          description={
            removeTarget ? (
              <>
                Remove <strong>{removeTarget.email}</strong> from{' '}
                <strong>{selectedProject.name}</strong>?
              </>
            ) : null
          }
          confirmLabel="Remove"
          tone="danger"
          onConfirm={() => {
            if (removeTarget) removeMember(removeTarget);
          }}
        />
      </AdminSceneShell>
    );
  }

  return (
    <AdminSceneShell
      sceneName="ProjectsScene"
      title="Project access"
      description="Assign cross-tenant project membership — viewer, member, or admin."
      stackClassName="space-y-4"
    >
      <Commentable name="Filters">
        <div>
          <AdminBulkActionRow left={<span>{filteredProjects.length} projects</span>}>
            <label htmlFor="project-access-search" className="sr-only">
              Search
            </label>
            <input
              id="project-access-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Project name or owner email"
              className={cn('w-full min-w-[12rem] sm:w-64', FIELD_INPUT)}
            />
          </AdminBulkActionRow>
        </div>
      </Commentable>

      <Commentable name="Table">
        <div>
          <AdminSectionCard>
            {filteredProjects.length === 0 ? (
              <EmptyState
                embedded
                icon={<FolderKey className="h-8 w-8 text-zinc-400" />}
                title="No projects match"
              />
            ) : (
              <Table wrap={false}>
                <THead>
                  <TR>
                    <TH>Name</TH>
                    <TH>Owner</TH>
                    <TH>Members</TH>
                    <TH>Threads</TH>
                    <TH>Created</TH>
                  </TR>
                </THead>
                <TBody>
                  {filteredProjects.map((project) => (
                    <TR key={project.id}>
                      <TD>
                        <button
                          type="button"
                          onClick={() => setSelectedProjectId(project.id)}
                          className="text-left font-medium text-zinc-900 underline-offset-2 hover:underline dark:text-zinc-100"
                        >
                          {project.name}
                        </button>
                      </TD>
                      <TD>{project.ownerEmail}</TD>
                      <TD>{project.memberCount}</TD>
                      <TD>{projectThreadCount(project)}</TD>
                      <TD>{formatAdminDate(project.createdAt)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </AdminSectionCard>
        </div>
      </Commentable>
    </AdminSceneShell>
  );
}
